import React, { type ReactNode, useEffect, useState } from 'react'

import type { Project } from '../../../../types/Project'
import type { WorkId } from '../../../../types/WorkId'
import { useAppState } from '../AppContext'
import { useProjectLoader } from '../ProjectContext/useProjectLoader'

export interface ProjectsContextModel {
  projects: Project[]
  addWorkId: (workId: WorkId) => void
  removeWorkId: (workId: WorkId) => void
}

export const ProjectsContext = React.createContext({} as ProjectsContextModel)

const ProjectsContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [workIds, setWorkIds] = useState<WorkId[]>([] as WorkId[])
  const [projects, setProjects] = useState<Project[]>([] as Project[])

  const { isAppStateInitialized, initialWorkIds, culture } = useAppState()
  const { projects: loaderProjects, isSuccess: loaderIsReady } = useProjectLoader(culture, ...workIds)

  useEffect(() => {
    setWorkIds([...initialWorkIds])
  }, [initialWorkIds])

  // update the projects list once the queries finish
  useEffect(() => {
    if (!loaderIsReady) {
      return
    }
    setProjects(loaderProjects)
  }, [loaderIsReady, loaderProjects])

  const addWorkId = (workId: WorkId) => {
    setWorkIds((prevWorkIds) => [...prevWorkIds, workId])
  }

  const removeWorkId = (workId: WorkId) => {
    setWorkIds((prevWorkIds) => {
      if (prevWorkIds.length > 1) {
        return prevWorkIds.filter((id) => id !== workId)
      }
      return prevWorkIds
    })
  }

  return <>{isAppStateInitialized && <ProjectsContext.Provider value={{ projects, removeWorkId, addWorkId }}>{children}</ProjectsContext.Provider>}</>
}

export default ProjectsContextProvider
